/**
 * T-379: Prompt Suggestion Keyword Matcher
 *
 * Utility for matching and ranking prompt templates against context tokens
 * using whole-word, case-insensitive matching with basic plural handling.
 */

/**
 * Normalizes a string to tokens for matching:
 * - Lowercases
 * - Splits on whitespace and common punctuation
 * - Applies basic plural normalization (removes trailing 's' when length > 3)
 */
export function normalizeToTokens(text: string): string[] {
  if (!text || typeof text !== 'string') return [];

  return text
    .toLowerCase()
    .split(/[\s.,;:!?'"()\[\]{}<>\/\\|@#$%^&*+=~`\-_]+/)
    .filter((token) => token.length > 0)
    .map((token) => {
      // Basic plural normalization: remove trailing 's' for words > 3 chars
      if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) {
        return token.slice(0, -1);
      }
      return token;
    });
}

/**
 * Computes a match score between context tokens and a prompt title.
 * Returns the count of matching tokens (whole-word, case-insensitive).
 */
export function computeMatchScore(
  contextTokens: string[],
  promptTitle: string
): number {
  if (contextTokens.length === 0) return 0;

  const titleTokens = normalizeToTokens(promptTitle);
  if (titleTokens.length === 0) return 0;

  // Create a set for O(1) lookups
  const titleTokenSet = new Set(titleTokens);

  // Count matching tokens
  let score = 0;
  for (const token of contextTokens) {
    if (titleTokenSet.has(token)) {
      score++;
    }
  }

  return score;
}

/**
 * A suggested template with match score for ranking
 */
export interface ScoredSuggestedTemplate<T> {
  template: T;
  matchScore: number;
  lastUsedAt: string | null;
}

/**
 * Ranks suggested templates by:
 * 1. Match score (higher is better)
 * 2. Last used time (more recent is better)
 * 3. Template ID (stable tie-breaker)
 *
 * @param templates - Templates to rank
 * @param contextTokens - Normalized context tokens to match against
 * @param getTitle - Function to extract title from template
 * @param getLastUsedAt - Function to extract lastUsedAt from template
 * @param getId - Function to extract id from template
 * @param limit - Maximum number of results to return
 */
export function rankSuggestedTemplates<T>(
  templates: T[],
  contextTokens: string[],
  getTitle: (t: T) => string,
  getLastUsedAt: (t: T) => string | null,
  getId: (t: T) => number,
  limit: number = 5
): ScoredSuggestedTemplate<T>[] {
  // Score each template
  const scored: ScoredSuggestedTemplate<T>[] = templates.map((template) => ({
    template,
    matchScore: computeMatchScore(contextTokens, getTitle(template)),
    lastUsedAt: getLastUsedAt(template),
  }));

  // Sort by score (desc), then lastUsedAt (desc), then id (asc)
  scored.sort((a, b) => {
    // Primary: match score (higher is better)
    if (b.matchScore !== a.matchScore) {
      return b.matchScore - a.matchScore;
    }

    // Secondary: last used time (more recent is better)
    const aTime = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0;
    const bTime = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0;
    if (bTime !== aTime) {
      return bTime - aTime;
    }

    // Tertiary: stable tie-breaker by id
    return getId(a.template) - getId(b.template);
  });

  return scored.slice(0, limit);
}

/**
 * Extracts context tokens from different surfaces.
 */
export function getThreadsContextTokens(commandInput: string): string[] {
  return normalizeToTokens(commandInput);
}

export function getClientContextTokens(client: {
  firstName?: string;
  lastName?: string;
  name?: string;
  tags?: string[];
  type?: string;
  interests?: string[];
}): string[] {
  const parts: string[] = [];

  // Add name parts
  if (client.firstName) parts.push(client.firstName);
  if (client.lastName) parts.push(client.lastName);
  if (client.name) parts.push(client.name);

  // Add type/tags
  if (client.type) parts.push(client.type);
  if (client.tags) parts.push(...client.tags);
  if (client.interests) parts.push(...client.interests);

  return normalizeToTokens(parts.join(' '));
}

export function getTripContextTokens(trip: {
  name?: string;
  title?: string;
  destination?: string;
  destinations?: string[];
  status?: string;
}): string[] {
  const parts: string[] = [];

  // Add name/title
  if (trip.name) parts.push(trip.name);
  if (trip.title) parts.push(trip.title);

  // Add destinations
  if (trip.destination) parts.push(trip.destination);
  if (trip.destinations) parts.push(...trip.destinations);

  // Add status
  if (trip.status) parts.push(trip.status);

  return normalizeToTokens(parts.join(' '));
}
